import { AHQueryHandlerLoading, AHSeparator, Box, useBoxStyle } from "arkh";
import React, { Suspense, useCallback, useMemo, useState } from "react";
import { TodoCreateScreenProps } from "./TodoCreateNavigator";
import {
  RichDescriptionInputMedia,
  RichDescriptionInputText,
  TitleInput,
} from "../../components/RichDescriptionInput";

import { useTodoCreateAddImage } from "./useTodoCreateAddImage";

import { TodoEditDeadlinePopoverComponent } from "../TodoEdit/TodoEditNewMain/Popovers/TodoEditDeadlinePopover";
import { TodoEditPrioPopoverComponent } from "widgets/src/Todo/TodoPopovers/TodoEditPrioPopover";
import { LazyQueryNetworkBoundrary } from "../../components/mobile/QueryNetworkBoundrary";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { TodoCreateMainNewQuery } from "./__generated__/TodoCreateMainNewQuery.graphql";

import { DropFileZone, droppedFileToImage } from "../Files/DropFileZone";

import { useNavigationHeader } from "../../common/navigationHeaderOptions";
import { usePersistedEmployeeId } from "@/src/OS/Login/usePersistedEmployeeId";
import { useAlert } from "../../common/Alert";
import { TodoCreateMainNewMutation } from "./__generated__/TodoCreateMainNewMutation.graphql";
import { useHotkeys } from "../../useHotKeys";
import { KeyboardAwareScrollView } from "react-native-keyboard-controller";
import { notEmpty } from "@/src/utils";

import { useTodoCreateState } from "./TodoCreateState";
import { TodoCreateToolbar } from "./TodoCreateToolbar";
import { TodoTeamsPopover } from "../TodoEdit/TodoEditNewMain/Popovers/TodoTeamsPopover";
import { AttachedContent } from "@/src/components/LinkedContent/AttachedContent";
import { TodoAttachmentControlls } from "../TodoEdit/TodoEditNewMain/TodoAttachmentControlls";
import { TodoCreateAssigneesPopover } from "../TodoEdit/TodoEditNewMain/Popovers/TodoAssigneesPopover/TodoCreateAssigneesPopover";
import { TodoTagsDialog } from "widgets/src/Todo/TodoDialogs/TodoTagsDialog";

export function TodoCreateMainNew(props: TodoCreateScreenProps<"Main">) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fetchKey) => <InnerTodoCreateMainNew {...props} fetchKey={fetchKey} />}
    </LazyQueryNetworkBoundrary>
  );
}

function InnerTodoCreateMainNew(
  props: TodoCreateScreenProps<"Main"> & { fetchKey: string }
) {
  const { state, setState, openFeature, setOpenFeature } = useTodoCreateState();
  const {
    teamIds,
    employeeIds,
    roleIds,
    priority,
    expireAt,
    tagIds,
    title,
    body,
    linkedProductIds,
  } = state;

  const data = useLazyLoadQuery<TodoCreateMainNewQuery>(
    graphql`
      query TodoCreateMainNewQuery(
        $categoryId: ID!
        $teamIds: [ID!]!
        $productIds: [ID!]!
      ) {
        ...TodoCreateToolbar_data

        storeProductsByIds(ids: $productIds) {
          ...AttachedContent_products
        }

        todoCategory(id: $categoryId) {
          tags {
            id
            title
            color
            ...TodoToolbarButtonTags_tags
          }
        }
        currentStore {
          me {
            employee {
              id
            }
          }
        }
      }
    `,
    {
      categoryId: props.route.params.categoryId,
      teamIds: teamIds,
      productIds: linkedProductIds,
    },
    { fetchKey: props.fetchKey, fetchPolicy: "store-and-network" }
  );

  const { currentEmployeeId } = usePersistedEmployeeId();

  const remoteEmployeeId = useMemo(() => {
    return data.currentStore?.me?.employee?.id;
  }, [data.currentStore?.me?.employee?.id]);

  const [_createTodo, loading] = useMutation<TodoCreateMainNewMutation>(graphql`
    mutation TodoCreateMainNewMutation(
      $todo: CreateTodoItemInput!
      $connections: [ID!]!
      $employeeId: ID
    ) {
      createTodoItem(todo: $todo, employeeId: $employeeId) {
        todo @prependNode(connections: $connections, edgeTypeName: "TodoEdge") {
          id
          ordinal

          createdAtDate {
            isoString
          }
          ...TodoListItem_todo
        }
      }
    }
  `);

  const params = props.route.params;

  const employeeId = useMemo(() => {
    return currentEmployeeId ?? remoteEmployeeId;
  }, [currentEmployeeId, remoteEmployeeId]);
  const isAssignedToMe =
    employeeId && employeeIds && employeeIds.includes(employeeId);

  const [images, setImages] = useState<
    {
      id?: string;
      uri: string;
      width: number;
      height: number;
      mediaType: "image";
    }[]
  >([]);

  const Alert = useAlert();
  const disabled = !(title && title.length > 0);

  const createTodo = useCallback(() => {
    if (disabled) return;
    _createTodo({
      onError: () => {
        Alert.error("Error occurred when trying to create todo");
      },
      onCompleted: () => {
        props.navigation.goBack();
      },
      variables: {
        employeeId: currentEmployeeId,
        todo: {
          productIds: linkedProductIds,
          priority: priority ?? "DEFAULT",
          allTeams: teamIds.length === 0,
          teamIds: teamIds,
          title: title,
          body: body ?? "",

          expireAt: expireAt ? expireAt.toISOString() : undefined,

          categoryId: params.categoryId,
          tagIds: tagIds,
          imageIds: images.map((image) => image.id).filter(notEmpty),
          assignedEmployeeIds: employeeIds,
          assignedRoleIds: roleIds,
        },
        connections: [
          ...(params.connectionId ? [params.connectionId] : []),
          ...(params.assignedToMeConnectionId && isAssignedToMe
            ? [params.assignedToMeConnectionId]
            : []),
        ],
      },
    });
  }, [
    Alert,
    _createTodo,
    body,
    currentEmployeeId,
    disabled,
    employeeIds,
    roleIds,
    expireAt,
    images,
    isAssignedToMe,
    linkedProductIds,
    params.assignedToMeConnectionId,
    params.categoryId,
    params.connectionId,
    priority,
    props.navigation,
    tagIds,
    teamIds,
    title,
  ]);

  useHotkeys("shift+enter", createTodo, {
    enableOnContentEditable: true,
    preventDefault: true,
  });

  useNavigationHeader({
    type: "CREATE",
    disabled: !(title && title.length > 0),
    onSave() {
      createTodo();
    },
    onCancel: () => {
      props.navigation.goBack();
    },
    saveLoading: loading,
    title: "",
    defaultContentStyle: true,
  });

  const onDismissFeature = useCallback(() => {
    setOpenFeature(null);
  }, [setOpenFeature]);

  const uploadImage = useTodoCreateAddImage((id, uri) => {
    setImages((prev) =>
      prev.map((image) => (image.uri === uri ? { ...image, id } : image))
    );
  });

  const onAddImage = useCallback(
    async (image: { uri: string; width: number; height: number }) => {
      setImages((prev) => [
        ...prev,
        { ...image, mediaType: "image", id: undefined },
      ]);
      uploadImage(image.uri, { width: image.width, height: image.height });
    },
    [uploadImage]
  );

  const contentContainerStyle = useBoxStyle({
    color: "surface-background-light",
  });

  const setTitle = useCallback(
    (newTitle?: string | undefined) => {
      setState((prev) => ({ ...prev, title: newTitle ?? "" }));
    },
    [setState]
  );

  const setBody = useCallback(
    (newBody?: string | undefined) => {
      setState((prev) => ({ ...prev, body: newBody }));
    },
    [setState]
  );

  return (
    <DropFileZone
      deactivated={false}
      type="IMAGE"
      onDropFilesNew={async (files) => {
        await Promise.all(
          files.map(async ({ file, abstractType }) => {
            if (abstractType === "IMAGE") {
              const { width, height, uri } = await droppedFileToImage(file);
              onAddImage({ uri, width, height });
            }
          })
        );
      }}
      maxSize={20000000}
    >
      <Suspense fallback={<AHQueryHandlerLoading />}>
        <KeyboardAwareScrollView
          contentInsetAdjustmentBehavior="automatic"
          keyboardShouldPersistTaps="handled"
          contentContainerStyle={contentContainerStyle}
          style={{
            flex: 1,
          }}
        >
          <Box color="surface-primary">
            <TitleInput title={title} setTitle={setTitle} autoFocus />
            <AHSeparator />
            <RichDescriptionInputText
              description={body ?? ""}
              setDescription={setBody}
              readOnly={false}
            />
            <RichDescriptionInputMedia
              media={images.map((image) => ({
                image: {
                  type: "local",
                  uri: image.uri,
                },
                width: image.width,
                height: image.height,
                onDelete: () => {
                  setImages((prev) => prev.filter((i) => i.uri !== image.uri));
                },
              }))}
            />
            <AttachedContent
              files={[]}
              products={data.storeProductsByIds?.filter(notEmpty) ?? []}
              openFile={({ fileId }) => {
                props.navigation.navigate("File", {
                  id: fileId,
                });
              }}
              openProduct={(productId) => {
                props.navigation.navigate("Product", {
                  id: productId,
                });
              }}
              unlinkProduct={(productId) => {
                setState((prev) => ({
                  ...prev,
                  linkedProductIds: state.linkedProductIds.filter(
                    (id) => id !== productId
                  ),
                }));
              }}
            />
            <TodoAttachmentControlls
              highlightCamera={false}
              onImageAdd={onAddImage}
              onRequestLinkProduct={() => {
                props.navigation.navigate("TodoCreateLinkProduct", {});
              }}
            />

            <TodoCreateToolbar
              openFeature={setOpenFeature}
              hasTags={data.todoCategory.tags.length > 0}
              data={data}
              allTags={data.todoCategory.tags}
            />
          </Box>
        </KeyboardAwareScrollView>

        <TodoEditDeadlinePopoverComponent
          expireAt={expireAt ? expireAt : null}
          visible={openFeature === "DEADLINE"}
          onClose={onDismissFeature}
          setExpireAt={(newExpireAt) => {
            setState((prev) => ({
              ...prev,
              expireAt: newExpireAt ?? undefined,
            }));
          }}
        />
        <TodoTagsDialog
          categoryTags={data.todoCategory.tags}
          selectedTagIds={tagIds}
          visible={openFeature === "TAG"}
          onClose={onDismissFeature}
          setSelectedTagIds={(tagIds) => {
            setState((prev) => ({
              ...prev,
              tagIds,
            }));
          }}
        />
      </Suspense>

      <TodoEditDeadlinePopoverComponent
        expireAt={expireAt ? expireAt : null}
        visible={openFeature === "DEADLINE"}
        onClose={onDismissFeature}
        setExpireAt={(newExpireAt) => {
          setState((prev) => ({
            ...prev,
            expireAt: newExpireAt ?? undefined,
          }));
        }}
      />
      <TodoEditPrioPopoverComponent
        prio={priority ?? "DEFAULT"}
        onClose={onDismissFeature}
        visible={openFeature === "PRIORITY"}
        setPrio={(prio) => {
          setState((prev) => ({ ...prev, priority: prio }));
        }}
      />
      <LazyQueryNetworkBoundrary>
        {(fk) => (
          <TodoCreateAssigneesPopover
            visible={openFeature === "ASSIGNEE"}
            onClose={onDismissFeature}
            fetchKey={fk}
          />
        )}
      </LazyQueryNetworkBoundrary>
      <LazyQueryNetworkBoundrary>
        {(fk) => (
          <TodoTeamsPopover
            fetchKey={fk}
            selectedTeamIds={teamIds}
            visible={openFeature === "TEAM"}
            onClose={onDismissFeature}
            setSelectedTeamIds={(selectedTeamIds) => {
              setState((prev) => ({ ...prev, teamIds: selectedTeamIds }));
            }}
          />
        )}
      </LazyQueryNetworkBoundrary>
    </DropFileZone>
  );
}
